import styled from 'styled-components';
import { Button, Container, lightColors } from '@tryhackme/thm-ui-components';
import { TooltipArrow, TooltipContent } from '@radix-ui/react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    justify-content: space-between;
    flex-direction: row;
  }
`;

export const StyledPublicProfileHeaderWrapper = styled.div`
  background: ${({ theme }) => theme.colors.black};
`;

export const StyledHeaderContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  align-items: center;
  margin-top: 5.6rem;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: row;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    margin-top: 0;
  }
`;

export const StyledBasicInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  justify-content: flex-end;
  width: 100%;
  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    margin-right: 1rem;
  }
`;

export const StyledCrownWrapper = styled.div`
  position: absolute;
  top: -4.4rem;
  left: 0.8rem;
  z-index: 1;
`;

export const StyledPremiumUserWrapper = styled.div`
  position: absolute;
  right: 0;
  z-index: 1;
`;

export const StyledAvatarContainer = styled.div`
  position: relative;
  border-radius: 50%;
  width: 17.8rem;
  height: 17.8rem;

  > div {
    width: 17.8rem;
    height: 17.8rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    margin: 0;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    margin: auto;
  }
`;

export const StyledNameLevelCountryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  flex-wrap: wrap;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: row;
  }
`;

export const StyledLevelCountryContainer = styled.div`
  display: flex;
  gap: 1.6rem;
  justify-content: center;
`;

export const StyledAboutText = styled.span`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: ${({ theme }) => theme.fonts.sizes.medium};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 2.6rem;
  color: ${({ theme }) => theme.colors.white};
  margin-left: 1rem;
  text-align: center;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    text-align: left;
    margin-left: 0;
  }
`;

export const StyledSocialNetworksWrapper = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: center;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    justify-content: flex-start;
  }
`;

export const StyledCountryFlagContainer = styled.img`
  margin-top: auto;
  margin-bottom: auto;
  object-fit: cover;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 0.1rem solid ${({ theme }) => theme.colors.secondary.lighter};
  background: ${({ theme }) => theme.colors.secondary.lighter};
`;

export const StyledNameText = styled.span`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  line-height: 3.68rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
`;

export const StyledLevelText = styled.span`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  line-height: 3.68rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: default;
`;

export const StyledBadgesButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  flex-wrap: wrap;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    flex-direction: row;
  }
`;

export const StyledButton = styled(Button)`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 1.6rem;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.black};
  border-color: ${({ theme }) => theme.colors.secondary.lighter};
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.secondary.lighter};
    color: ${() => lightColors.text.main};

    path {
      stroke: ${() => lightColors.text.main};
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    width: unset;
  }
`;

export const StyledStatisticsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto auto;
  gap: 1.6rem;
  padding-bottom: 3.2rem;

  @media screen and (min-width: ${({ theme }) => theme.sizes.md}) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
`;

export const StyledStatisticBox = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 0.8rem;
  padding: 1.6rem 2.4rem;
  background: ${({ theme }) => theme.colors.secondary.main};
  height: 10.2rem;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.sizes.lg}) {
    width: 20.6rem;
  }
`;

export const StyledTopContainer = styled.div`
  background: ${({ theme }) => theme.colors.secondary.light};
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.small};
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  line-height: 1.6rem;
  border-radius: 0 0.5rem 0 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  height: 3rem;
  width: 7.5rem;
  padding-left: 0.4rem;
`;

export const StyledStatisticsBoxText = styled.div`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 2rem;
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 2.5rem;
  color: ${({ theme }) => theme.colors.white};
`;

export const StyledStatisticsBoxIconNumberContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

export const StyledStatisticsBoxIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
`;

export const StyledStatisticsBoxNumber = styled.span`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.subTitle};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  line-height: 3.68rem;
  color: ${({ theme }) => theme.colors.white};
`;

export const StyledTooltipContentWrapper = styled(TooltipContent)`
  display: flex;
  gap: 0.3rem;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.radius.small};
  box-shadow: ${({ theme }) => theme.boxShadow.light};
  opacity: 1;
  z-index: 1001;
  padding: 1.2rem;
`;

export const StyledTooltipArrow = styled(TooltipArrow)`
  fill: ${({ theme }) => theme.colors.background.tertiary};
`;

export const StyledBadgeIdIcon = styled(FontAwesomeIcon)`
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  line-height: 1.9rem;
  text-align: center;
`;

export const StyledLevelsWrapper = styled.div`
  margin-top: 2.4rem;
`;
